import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { MoodsDistributionBarComponent } from "../moods-distribution-bar/moods-distribution-bar.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Kudos, Mood, TimelineItem, TimelineItemDialogService, TipComponent, Topic } from "@teammood/timeline";
import { KudosCardComponent } from "../timeline-item/kudos/kudos-card.component";
import { DaysService } from "./days.service";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { LoggerService } from "@teammood/shared/util/logger";
import { filter, takeUntil, tap } from "rxjs";
import { MoodCardComponent } from "../timeline-item/moods/mood-card/mood-card.component";
import { TopicCardComponent } from "../timeline-item/topics/topic-card/topic-card.component";
import { BadMoodStreakComponent } from "../timeline-item/streaks/bad-mood-streak/bad-mood-streak.component";
import { BadMoodPassComponent } from "../timeline-item/streaks/bad-mood-pass/bad-mood-pass.component";
import { TimelineDay } from "../timeline.types";
import { UserInfos } from "../../shared/ui/username-icon/user-infos.type";
import { ClosedPollResultCardComponent } from "../timeline-item/poll-result/closed-poll-result/closed-poll-result.component";
import { ScalePollResultCardComponent } from "../timeline-item/poll-result/scale-poll-result/scale-poll-result.component";
import { AgreeDisagrePollResultCardComponent } from "../timeline-item/poll-result/agree-disagree-poll-result/agree-disagree-poll-result.component";
import { FiveStarsPollResultCardComponent } from "../timeline-item/poll-result/five-stars-poll-result/five-stars-poll-result.component";
import { CommonModule } from "@angular/common";

@Component({
    selector: "tm-daily-overview",
    templateUrl: "./daily-overview.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MoodsDistributionBarComponent,
        TranslateModule,
        KudosCardComponent,
        MoodCardComponent,
        TipComponent,
        TopicCardComponent,
        BadMoodStreakComponent,
        BadMoodPassComponent,
        ClosedPollResultCardComponent,
        ScalePollResultCardComponent,
        AgreeDisagrePollResultCardComponent,
        FiveStarsPollResultCardComponent,
        CommonModule
    ]
})
export class DailyOverviewComponent implements OnChanges {

    @Input({required: true}) day: TimelineDay;

    @Input({required: true}) teamId: string;

    @Input() userInfos: UserInfos;

    @Input() filterOption: string = 'all';

    @Output() 
    updateUserRequest: EventEmitter<void> = new EventEmitter<void>();

    filteredItems: TimelineItem[] = [];
    protected moodItems: Mood[];

    constructor(private daysService: DaysService,
                private snackBarService: SnackBarService,
                private logger: LoggerService,
                private translateService: TranslateService,
                private timelineItemDialogService: TimelineItemDialogService,
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        this.filteredItems = this.sortByCommentLength(this.day.values);

        if(changes.day?.currentValue){
            this.updateMoodsDistributionBar();
        }
        if(changes.filterOption?.currentValue){
            this.filteredItems = this.sortByCommentLength(this.daysService.filterItems(this.day.values, this.filterOption));
        }
    }

    private updateMoodsDistributionBar() {
        this.moodItems = this.day?.values ? this.day.values.filter((timelineItem) => timelineItem.itemType === 'moodItem').map(ti => ti as Mood): [];
    }

    sortByCommentLength(timelineItems: TimelineItem[]) {
        return this.daysService.sortByCommentLength(timelineItems)
    }

    onEditKudos(kudos: Kudos){
        if (kudos && kudos.message && kudos.kudosType) {
            this.daysService.updateTimelineItem(kudos, 'kudosItem').then((success: any) => {
                this.reloadDayAfterAction(success.day);
                this.snackBarService.success(this.translateService.instant('kudos_updated'));
            }).catch(error => {
                this.logger.error('Error updating kudos:', error);
                this.snackBarService.error(error);
            });
        }
    }

    onEditMood(mood: Mood) {
       if (mood) {
           this.daysService.updateMood(mood).then((success: any) => {
               this.reloadDayAfterAction(success.day);
               this.snackBarService.success(this.translateService.instant('mood_updated'));

           }).catch(error => {
               this.logger.error('Error updating mood:', error);
               this.snackBarService.error(error);
           });
       }
    }

    onEditTopic(topic: Topic) {
        if (topic && topic.title) {
            this.daysService.updateTimelineItem(topic, 'topicItem').then((success: any) => {
                this.reloadDayAfterAction(success.day)
                this.snackBarService.success(this.translateService.instant('topic_updated'));

            }).catch(error => {
                this.logger.error('Error updating topic:', error);
                this.snackBarService.error(error);
            });
        }
    }

    private reloadDayAfterAction(newDay: TimelineDay){
        if(newDay?.values.length > 0){
            this.day = newDay;
            this.filteredItems = this.sortByCommentLength(this.daysService.filterItems(this.day.values, this.filterOption));
        } else {
            this.day = undefined;            
            this.filteredItems = undefined;

        }
        this.updateMoodsDistributionBar();
    }

    onDeletionValidation(timelineItem: TimelineItem) {
        if (timelineItem) {
            this.daysService.deleteTimelineItem(timelineItem).then((success: any) => {

                this.updateUserRequest.emit();

                this.reloadDayAfterAction(success.day);

                switch (timelineItem.itemType){
                    case 'moodItem':
                        this.snackBarService.success(this.translateService.instant('mood_deleted'));
                        break;
                    case 'topicItem':
                        this.snackBarService.success(this.translateService.instant('topic_deleted'));
                        break;
                    case 'kudosItem':
                        this.snackBarService.success(this.translateService.instant('kudos_deleted'));
                        break;
                }

            }).catch((error) => {
                switch (timelineItem.itemType){
                    case 'moodItem':
                        this.logger.error('Error deleting mood:', error);
                        this.snackBarService.error('Error deleting mood');
                        break;
                    case 'topicItem':
                        this.logger.error('Error deleting topic:', error);
                        this.snackBarService.error('Error deleting topic');
                        break;
                    case 'kudosItem':
                        this.logger.error('Error deleting kudos:', error);
                        this.snackBarService.error('Error deleting Kudos');
                        break;
                    default:
                        this.logger.error('Error deleting timelineItem:', error);
                }
            });
        }
    }

    async onDeleteTimelineItem(timelineItem: TimelineItem) {
        const dialogRef = await this.timelineItemDialogService.delete(timelineItem);

        dialogRef.beforeClosed().pipe(
            filter(shouldDelete => !!shouldDelete),
            tap((_) => this.onDeletionValidation(timelineItem)),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    async onModerateTimelineItem(timelineItem: TimelineItem) {
        const dialogRef = await this.timelineItemDialogService.moderate();

        dialogRef.beforeClosed().pipe(
            filter(why => !!why),
            tap((why) => {
                this.daysService.moderateTimelineItem(timelineItem, why).then((response: any) => {
                    this.reloadDayAfterAction(response.day);

                    this.updateUserRequest.emit();

                    this.snackBarService.success('moderation_success');
                }).catch(error => {
                    switch (timelineItem.itemType){
                        case 'moodItem':
                            this.logger.error('Error moderating mood:', error);
                            this.snackBarService.error('Error moderating mood');
                            break;
                        case 'topicItem':
                            this.logger.error('Error moderating topic:', error);
                            this.snackBarService.error('Error moderating topic');
                            break;
                        case 'kudosItem':
                            this.logger.error('Error moderating kudos:', error);
                            this.snackBarService.error('Error moderating kudos');
                            break;
                        default:
                            this.logger.error('Error moderating timelineItem:', error);
                    }
                });
            }),
            takeUntil(dialogRef.afterClosed())
          ).subscribe();
    }
}